import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, PlusIcon, PencilIcon, EyeIcon, EyeOffIcon, RepeatIcon, CalendarIcon, ClockIcon, MapPinIcon, LockIcon, AlertTriangleIcon, CheckIcon, XIcon, TrashIcon, UserPlusIcon, ClipboardCheckIcon, UserIcon, PackageIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ActivityForm } from './ActivityForm';
import { AttendanceSheet } from './AttendanceSheet';
import { paymentMeta } from './bookingHelpers';
import { ACTIVITIES, REGISTRANTS } from '../../data/loisirs';
import { RESOURCES } from '../../data/windsor';
import { resolvePayment } from '../../types/loisirs';
import type { Activity, ActivitySlot, ActivityStatus, Registrant } from '../../types/loisirs';
interface Props {
  /** Search term owned by the Manage tab toolbar. */
  query: string;
  /** True while the "New activity" flow is open. */
  creating: boolean;
  /** Closes the creation flow, whether saved or cancelled. */
  onCloseCreate: () => void;
}
const WEEKDAYS: Record<number, {
  fr: string;
  en: string;
}> = {
  1: {
    fr: 'Lundi',
    en: 'Monday'
  },
  2: {
    fr: 'Mardi',
    en: 'Tuesday'
  },
  3: {
    fr: 'Mercredi',
    en: 'Wednesday'
  },
  4: {
    fr: 'Jeudi',
    en: 'Thursday'
  },
  5: {
    fr: 'Vendredi',
    en: 'Friday'
  },
  6: {
    fr: 'Samedi',
    en: 'Saturday'
  },
  7: {
    fr: 'Dimanche',
    en: 'Sunday'
  }
};
export function ActivityRegistrations({
  query,
  creating,
  onCloseCreate
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [activities, setActivities] = useState<Activity[]>(ACTIVITIES);
  const [openId, setOpenId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [hidden, setHidden] = useState<string[]>(ACTIVITIES.filter(a => !a.visible).map(a => a.id));
  const [addingTo, setAddingTo] = useState(false);
  const [attendanceFor, setAttendanceFor] = useState<string | null>(null);
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });

  /** "Lundi 16 h – 17 h" — one line per day the activity meets. */
  const slotLabel = (slot: ActivitySlot, activity: Activity) => {
    const time = lang === 'fr' ? `${slot.startTime.replace(':', ' h ').replace(' h 00', ' h')} – ${slot.endTime.replace(':', ' h ').replace(' h 00', ' h')}` : `${slot.startTime} – ${slot.endTime}`;
    if (activity.recurrence === 'once' && slot.date) {
      return `${new Date(slot.date).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })}, ${time}`;
    }
    return `${WEEKDAYS[slot.weekday][lang]}, ${time}`;
  };
  const statusMeta: Record<ActivityStatus, {
    label: string;
    className: string;
  }> = {
    open: {
      label: t('Ouvert', 'Open'),
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    waitlist: {
      label: t('Liste d’attente', 'Waitlist'),
      className: 'bg-amber-50 text-amber-700 border-amber-200'
    },
    full: {
      label: t('Complet', 'Full'),
      className: 'bg-red-50 text-red-700 border-red-200'
    },
    closed: {
      label: t('Fermé', 'Closed'),
      className: 'bg-slate-100 text-slate-600 border-slate-200'
    }
  };
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activities;
    return activities.filter(a => a.name[lang].toLowerCase().includes(q) || a.type[lang].toLowerCase().includes(q) || a.venue[lang].toLowerCase().includes(q));
  }, [query, lang, activities]);
  const activity = activities.find(a => a.id === openId);

  /** Creation and edition share the same form. */
  const edited = activities.find(a => a.id === editingId);
  if (creating || edited) {
    return <ActivityForm activity={edited} existing={activities} onCancel={() => {
      setEditingId(null);
      onCloseCreate();
    }} onSave={saved => {
      setActivities(prev => prev.some(a => a.id === saved.id) ? prev.map(a => a.id === saved.id ? saved : a) : [saved, ...prev]);
      setHidden(prev => saved.visible ? prev.filter(id => id !== saved.id) : prev.includes(saved.id) ? prev : [...prev, saved.id]);
      setEditingId(null);
      onCloseCreate();
      setOpenId(saved.id);
    }} data-id="element-2259" data-mp="k8d1gnjy5zt1" />;
  }

  // ------------------------------------------------------------- DETAIL VIEW
  if (activity) {
    const roster: Registrant[] = REGISTRANTS.filter(r => r.activityId === activity.id);
    const confirmed = roster.filter(r => r.status === 'confirmed');
    const waiting = roster.filter(r => r.status === 'waitlist');
    if (attendanceFor === activity.id) {
      return <AttendanceSheet activity={activity} roster={confirmed} onBack={() => setAttendanceFor(null)} data-id="element-2260" data-mp="1kw5oep2kfasw" />;
    }
    return <div className="space-y-4 animate-in fade-in duration-200" data-id="element-2261" data-mp="1byd8bip0rz4g">
        <button onClick={() => {
        setOpenId(null);
        setAddingTo(false);
      }} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-[#4F46E5] transition-colors" data-id="element-2262" data-mp="16z0nf3hpl24i">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-2263" data-mp="1zny45269uqv5" />
          {t('Retour aux activités', 'Back to activities')}
        </button>

        {/* Activity header */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-2264" data-mp="1py4u45ls2jt0">
          <div className="flex items-start justify-between gap-4 flex-wrap" data-id="element-2265" data-mp="2ncqtgfse20gs">
            <div className="min-w-0" data-id="element-2266" data-mp="2nettztr63bzr">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-2267" data-mp="2ilh4lhenz15y">
                <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 text-xs font-medium border border-slate-200" data-id="element-2268" data-mp="2ik60fghb3reb">
                  {activity.type[lang]}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusMeta[activity.status].className}`} data-id="element-2269" data-mp="36jgk8051tcyi">
                  {statusMeta[activity.status].label}
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-id="element-2270" data-mp="2iri3h0mtypdz">
                  {activity.recurrence === 'weekly' ? <>
                      <RepeatIcon className="w-3.5 h-3.5" data-id="element-2271" data-mp="29r36v54f1nv6" />
                      {t('Hebdomadaire', 'Weekly')}
                    </> : <>
                      <CalendarIcon className="w-3.5 h-3.5" data-id="element-2272" data-mp="2duhxsqtw7one" />
                      {t('Séance unique', 'One-time')}
                    </>}
                </span>
              </div>
              <h2 className="text-xl font-bold text-[#0F172A] font-heading mt-2" data-id="element-2273" data-mp="byz6mfz10y9g">
                {activity.name[lang]}
              </h2>
              <p className="inline-flex items-center gap-1.5 text-sm text-slate-500 mt-1" data-id="element-2274" data-mp="1sik2no9olseb">
                <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-2275" data-mp="269p7d4rwpavi" />
                {activity.venue[lang]} · {activity.ageRange} {t('ans', 'yrs')} ·{' '}
                {activity.season[lang]}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0" data-id="element-2276" data-mp="2g60l7z75a8s2">
              <button onClick={() => setHidden(prev => prev.includes(activity.id) ? prev.filter(id => id !== activity.id) : [...prev, activity.id])} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2277" data-mp="2b8ik2uzdcxum">
                {hidden.includes(activity.id) ? <>
                    <EyeOffIcon className="w-4 h-4" data-id="element-2278" data-mp="4x5anb7lty96" />
                    {t('Masquée', 'Hidden')}
                  </> : <>
                    <EyeIcon className="w-4 h-4" data-id="element-2279" data-mp="dskztexezp8f" />
                    {t('Visible', 'Visible')}
                  </>}
              </button>
              <button onClick={() => setEditingId(activity.id)} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2280" data-mp="3i5rklhryevn0">
                <PencilIcon className="w-4 h-4" data-id="element-2281" data-mp="3r8iuve93fld2" />
                {t('Modifier l’activité', 'Edit activity')}
              </button>
              <button onClick={() => setAttendanceFor(activity.id)} className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-2282" data-mp="16gqkk1ogxe89">
                <ClipboardCheckIcon className="w-4 h-4" data-id="element-2283" data-mp="2qy9lrm9ah525" />
                {t('Feuille de présences', 'Attendance sheet')}
              </button>
              <button onClick={() => setAddingTo(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-2284" data-mp="2m688py4arab2">
                <UserPlusIcon className="w-4 h-4" data-id="element-2285" data-mp="2881in45laely" />
                {t('Inscrire un citoyen', 'Register a citizen')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100" data-id="element-2286" data-mp="23ai6qawd6dx6">
            <div data-id="element-2287" data-mp="1ybyksahrt24t">
              <p className="text-xs text-slate-500" data-id="element-2288" data-mp="1h21o8zj4wio">
                {t('Places occupées', 'Spots filled')}
              </p>
              <p className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-2289" data-mp="3soodwq0be7b5">
                {confirmed.length || activity.registered} / {activity.capacity}
              </p>
            </div>
            <div data-id="element-2290" data-mp="6satdtd6zb8i">
              <p className="text-xs text-slate-500" data-id="element-2291" data-mp="6qpwvy7bhi80">
                {t('Liste d’attente', 'Waitlist')}
              </p>
              <p className={`text-lg font-bold font-heading ${waiting.length > 0 ? 'text-amber-600' : 'text-[#0F172A]'}`} data-id="element-2292" data-mp="lo0gxvy0zwfc">
                {waiting.length || activity.waitlisted}
              </p>
            </div>
            <div data-id="element-2293" data-mp="3k7qq8qinej2l">
              <p className="text-xs text-slate-500" data-id="element-2294" data-mp="3k88p7uwp3be7">
                {t('Tarif résident', 'Resident rate')}
              </p>
              <p className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-2295" data-mp="3u4lmf2rg87dp">
                {money(activity.priceResident)}
              </p>
            </div>
            <div data-id="element-2296" data-mp="36dg6knlidf3e">
              <p className="text-xs text-slate-500" data-id="element-2297" data-mp="36cwkcuots4jk">
                {t('Tarif non-résident', 'Non-resident rate')}
              </p>
              <p className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-2298" data-mp="1gxdjt3q0aeho">
                {money(activity.priceNonResident)}
              </p>
            </div>
          </div>

          {/* Slots */}
          <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-2299" data-mp="1707t3wlb0phz">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2300" data-mp="16xmrn8ox4bkr">
              {t('Horaires', 'Schedule')}
            </h3>
            <ul className="flex flex-wrap gap-2" data-id="element-2301" data-mp="1zper8ugujd2p">
              {activity.slots.map(slot => <li key={slot.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-2302" data-mp="24mewx49sadta">
                  <ClockIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2303" data-mp="1unz857y8bgmy" />
                  {slotLabel(slot, activity)}
                </li>)}
            </ul>
          </div>

          {(activity.resources ?? []).length > 0 && <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-2304" data-mp="j7sfyugc4t23">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2305" data-mp="2lw94iju1xf0q">
                {t('Ressources', 'Resources')}
              </h3>
              <ul className="flex flex-wrap gap-2" data-id="element-2306" data-mp="2c6h8dl6b0du5">
                {(activity.resources ?? []).map(need => {
              const resource = RESOURCES.find(r => r.id === need.resourceId);
              if (!resource) return null;
              return <li key={need.resourceId} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-2307" data-mp="1y66cg3q50924">
                      {resource.kind === 'humain' ? <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2308" data-mp="3nme3jr4qjtzt" /> : <PackageIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2309" data-mp="3io49xkynt1gb" />}
                      {resource.name[lang]}
                      {need.quantity > 1 && <span className="text-xs font-semibold text-slate-500 tabular-nums" data-id="element-2310" data-mp="3v4viwdzam30p">
                          × {need.quantity}
                        </span>}
                    </li>;
            })}
              </ul>
            </div>}

          {activity.prerequisites.length > 0 && <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-2311" data-mp="387acvei1of5c">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2312" data-mp="3c7klkyx81jnw">
                {t('Préalables', 'Prerequisites')}
              </h3>
              <ul className="space-y-1.5" data-id="element-2313" data-mp="3m5i7hfrlz4dc">
                {activity.prerequisites.map(pre => <li key={pre.id} className="flex items-start gap-2 text-sm text-slate-700" data-id="element-2314" data-mp="wg6gkwmtc8df">
                    {pre.blocking ? <LockIcon className="w-4 h-4 text-[#4F46E5] mt-0.5 flex-shrink-0" data-id="element-2315" data-mp="2abext1834wui" /> : <AlertTriangleIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-2316" data-mp="25dddkcyxarhg" />}
                    <span data-id="element-2317" data-mp="2jd5yfrnrqdhi">
                      {pre.label[lang]}
                      <span className="text-slate-400" data-id="element-2318" data-mp="2ecs0j2fvsjx7">
                        {' — '}
                        {pre.blocking ? t('bloquant', 'blocking') : t('avertissement', 'warning')}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>}
        </div>

        {/* Inline add form */}
        {addingTo && <div className="bg-white rounded-xl border border-[#4F46E5]/30 ring-1 ring-[#4F46E5]/10 p-5" data-id="element-2319" data-mp="2y22eaaw2guso">
            <div className="flex items-center justify-between gap-3 mb-4" data-id="element-2320" data-mp="3byxs0gn9jaf2">
              <h3 className="text-sm font-bold text-[#0F172A]" data-id="element-2321" data-mp="373ig3u8tfola">
                {t('Inscrire un citoyen', 'Register a citizen')}
              </h3>
              <button onClick={() => setAddingTo(false)} aria-label={t('Fermer', 'Close')} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-2322" data-mp="3vmu02jrheqt6">
                <XIcon className="w-4 h-4" data-id="element-2323" data-mp="3ln34sfv3k7s1" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-id="element-2324" data-mp="37whgucqsd7na">
              <div className="md:col-span-2" data-id="element-2325" data-mp="3cohneo1d2ysu">
                <label htmlFor="reg-name" className="block text-xs font-medium text-slate-600 mb-1" data-id="element-2326" data-mp="3cpall0m98fzd">
                  {t('Citoyen', 'Citizen')}
                </label>
                <input id="reg-name" type="text" placeholder={t('Rechercher dans bciti+…', 'Search in bciti+…')} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-2327" data-mp="32vtptr2k4qt2" />
              </div>
              <div data-id="element-2328" data-mp="pqx5bb6yo2xj">
                <label htmlFor="reg-slot" className="block text-xs font-medium text-slate-600 mb-1" data-id="element-2329" data-mp="pshwcqu2h3qn">
                  {t('Horaire', 'Time slot')}
                </label>
                <select id="reg-slot" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-2330" data-mp="1ifch7ousq54n">
                  {activity.slots.map(slot => <option key={slot.id} value={slot.id} data-id="element-2331" data-mp="14g33qwxz5p4d">
                      {slotLabel(slot, activity)}
                    </option>)}
                </select>
              </div>
              <div className="flex items-end" data-id="element-2332" data-mp="3p5aczmq4lkch">
                <button onClick={() => setAddingTo(false)} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-2333" data-mp="24p2pousgshnf">
                  <CheckIcon className="w-4 h-4" data-id="element-2334" data-mp="2ifo0rxbofnar" />
                  {activity.status === 'full' || activity.status === 'waitlist' ? t('Mettre en attente', 'Add to waitlist') : t('Confirmer', 'Confirm')}
                </button>
              </div>
            </div>
            {(activity.status === 'full' || activity.status === 'waitlist') && <p className="flex items-start gap-1.5 text-xs text-amber-700 mt-3" data-id="element-2335" data-mp="2wdw18f7x96qo">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-2336" data-mp="31cpi8hmf3fvl" />
                {t('L’activité est complète : la personne sera placée en liste d’attente.', 'This activity is full: the person will be placed on the waitlist.')}
              </p>}
          </div>}

        {/* Roster */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-2337" data-mp="34w7ulpet027o">
          <div className="px-5 py-4 border-b border-slate-100" data-id="element-2338" data-mp="3imt44nidazsw">
            <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-2339" data-mp="3dpb79vhmkb19">
              {t('Personnes inscrites', 'Registered people')}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-2340" data-mp="2pzywwsak9wxd">
              {confirmed.length} {t('confirmées', 'confirmed')} ·{' '}
              {waiting.length} {t('en attente', 'waiting')}
            </p>
          </div>

          {roster.length === 0 ? <p className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-2341" data-mp="2uy0hxirjs6bh">
              {t('Aucune inscription pour le moment.', 'No registration yet.')}
            </p> : <div className="overflow-x-auto" data-id="element-2342" data-mp="a7j91zcwauum">
              <table className="w-full text-sm text-left" data-id="element-2343" data-mp="1s5xocob4m473">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-2344" data-mp="1s4e99117hz6w">
                  <tr data-id="element-2345" data-mp="1x5tscnjtpvmn">
                    <th className="px-5 py-3" data-id="element-2346" data-mp="1x6bxyasdlm78">{t('Nom', 'Name')}</th>
                    <th className="px-5 py-3" data-id="element-2347" data-mp="1j6avws0dk8q6">{t('Horaire', 'Time slot')}</th>
                    <th className="px-5 py-3" data-id="element-2348" data-mp="1o3uaxu4wcpj6">{t('Résidence', 'Residency')}</th>
                    <th className="px-5 py-3" data-id="element-2349" data-mp="2btwwrsi7wz9c">{t('Paiement', 'Payment')}</th>
                    <th className="px-5 py-3" data-id="element-2350" data-mp="2bw1bl5hnnioe">{t('Statut', 'Status')}</th>
                    <th className="px-5 py-3 text-right" data-id="element-2351" data-mp="2fu8lbgdpbjq3">
                      {t('Actions', 'Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-2352" data-mp="272ewz7rxl4py">
                  {roster.map(person => {
                const slot = activity.slots.find(s => s.id === person.slotId);
                return <tr key={person.id} className="group hover:bg-slate-50/60" data-id="element-2353" data-mp="2zl2dsazw9xl8">
                        <td className="px-5 py-3.5" data-id="element-2354" data-mp="3fu1mzvw598v9">
                          <p className="font-medium text-slate-900" data-id="element-2355" data-mp="3ks3ft8jdgmjx">
                            {person.name}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-2356" data-mp="36zn8vwnco4dq">
                            {person.age} {t('ans', 'yrs')}
                            {person.guardian && ` · ${person.guardian}`}
                          </p>
                          {person.missing && <p className="flex items-start gap-1.5 text-xs text-amber-700 mt-1" data-id="element-2357" data-mp="321bvjbxujbqr">
                              <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-2358" data-mp="2o23z2qt2ihq9" />
                              {person.missing[lang]}
                            </p>}
                        </td>
                        <td className="px-5 py-3.5 text-slate-600" data-id="element-2359" data-mp="mbsfcz1tycpn">
                          {slot ? slotLabel(slot, activity) : '—'}
                        </td>
                        <td className="px-5 py-3.5 text-slate-600" data-id="element-2360" data-mp="2atturavmfb39">
                          {person.resident ? t('Résident', 'Resident') : t('Non-résident', 'Non-resident')}
                        </td>
                        <td className="px-5 py-3.5" data-id="element-2361" data-mp="2klznimtp5wji">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${paymentMeta(resolvePayment(person).status).className}`} data-id="element-2362" data-mp="2fsy35zaq14sj">
                            {paymentMeta(resolvePayment(person).status)[lang]}
                          </span>
                        </td>
                        <td className="px-5 py-3.5" data-id="element-2363" data-mp="194hryam9s382">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${person.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-id="element-2364" data-mp="1959mrj81lwxa">
                            {person.status === 'confirmed' ? t('Confirmée', 'Confirmed') : t('En attente', 'Waiting')}
                          </span>
                        </td>
                        <td className="px-5 py-3.5" data-id="element-2365" data-mp="3s53dfqnl9rpr">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-2366" data-mp="3s3s8vombkfxl">
                            {person.status === 'waitlist' && <button aria-label={t('Confirmer la place', 'Confirm spot')} title={t('Confirmer la place', 'Confirm spot')} className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors" data-id="element-2367" data-mp="9s5jc0dh9t3i">
                                <CheckIcon className="w-4 h-4" data-id="element-2368" data-mp="1u8d2qh80qbna" />
                              </button>}
                            <button aria-label={t('Retirer l’inscription', 'Remove registration')} title={t('Retirer', 'Remove')} className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-2369" data-mp="1qa5w4f79qqpt">
                              <TrashIcon className="w-4 h-4" data-id="element-2370" data-mp="2965gp4xx2rzd" />
                            </button>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </div>
      </div>;
  }

  // -------------------------------------------------------------- LIST VIEW
  return <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-2371" data-mp="2gfp9lnjsexj7">
      <div className="overflow-x-auto" data-id="element-2372" data-mp="2lhekn370fb7a">
        <table className="w-full text-sm text-left" data-id="element-2373" data-mp="2le8x3se14hdo">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-2374" data-mp="27hm0jqpznt8a">
            <tr data-id="element-2375" data-mp="2cgory8d1q0xs">
              <th className="px-5 py-3 font-medium" data-id="element-2376" data-mp="2cg6zlmti86v5">{t('Type', 'Type')}</th>
              <th className="px-5 py-3 font-medium" data-id="element-2377" data-mp="306asaz55mm7d">{t('Nom', 'Name')}</th>
              <th className="px-5 py-3 font-medium" data-id="element-2378" data-mp="346l8t5qkov0h">{t('Horaires', 'Schedule')}</th>
              <th className="px-5 py-3 font-medium" data-id="element-2379" data-mp="2qfg5ntnzxkle">{t('Lieu', 'Location')}</th>
              <th className="px-5 py-3 font-medium" data-id="element-2380" data-mp="2qdc4hjhhezot">
                {t('Réservations', 'Bookings')}
              </th>
              <th className="px-5 py-3 font-medium" data-id="element-2381" data-mp="3j43x66zc967e">{t('Prix', 'Price')}</th>
              <th className="px-5 py-3 font-medium" data-id="element-2382" data-mp="3j20uonh01kls">{t('Visible', 'Visible')}</th>
              <th className="px-5 py-3" data-id="element-2383" data-mp="3nwdfb0u7p1z4" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-2384" data-mp="136m56mr3vk0">
            {visible.map(item => {
            const fill = Math.round(item.registered / item.capacity * 100);
            const isVisible = !hidden.includes(item.id);
            return <tr key={item.id} onClick={() => setOpenId(item.id)} tabIndex={0} role="button" onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpenId(item.id);
              }
            }} className="group cursor-pointer hover:bg-slate-50/80 focus:outline-none focus:bg-indigo-50/50 transition-colors" data-id="element-2385" data-mp="5uyffoxakfdu">
                  <td className="px-5 py-4" data-id="element-2386" data-mp="13hnq0n6280bb">
                    <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-600 text-xs font-medium border border-slate-200 whitespace-nowrap" data-id="element-2387" data-mp="13kjn69a32kyp">
                      {item.type[lang]}
                    </span>
                  </td>

                  <td className="px-5 py-4" data-id="element-2388" data-mp="1al5biv76rpjv">
                    <p className="font-medium text-slate-900" data-id="element-2389" data-mp="1anzxws9rfkfx">{item.name[lang]}</p>
                    <p className="inline-flex items-center gap-1 text-xs text-slate-400 mt-0.5" data-id="element-2390" data-mp="1flj9fkf2pj46">
                      {item.recurrence === 'weekly' ? <>
                          <RepeatIcon className="w-3 h-3" data-id="element-2391" data-mp="mxnfgngw25b3" />
                          {t('Hebdomadaire', 'Weekly')}
                        </> : <>
                          <CalendarIcon className="w-3 h-3" data-id="element-2392" data-mp="wtqhltbqq3y1" />
                          {t('Séance unique', 'One-time')}
                        </>}
                      {' · '}
                      {item.ageRange} {t('ans', 'yrs')}
                    </p>
                  </td>

                  <td className="px-5 py-4" data-id="element-2393" data-mp="93mowh03bolt">
                    <ul className="space-y-0.5" data-id="element-2394" data-mp="1lercugfcgfj">
                      {item.slots.map(slot => <li key={slot.id} className="text-slate-600 whitespace-nowrap" data-id="element-2395" data-mp="3srpxv647e9e9">
                          {slotLabel(slot, item)}
                        </li>)}
                    </ul>
                  </td>

                  <td className="px-5 py-4 text-slate-600 max-w-[200px]" data-id="element-2396" data-mp="3esgig8ntnjbc">
                    <span className="block truncate" data-id="element-2397" data-mp="3etroyg8nj565">{item.venue[lang]}</span>
                  </td>

                  <td className="px-5 py-4" data-id="element-2398" data-mp="39zdpa1myibvo">
                    <div className="w-36" data-id="element-2399" data-mp="3536pyrp2fymp">
                      <div className="flex items-center justify-between text-xs mb-1" data-id="element-2400" data-mp="34zsnljgejpf9">
                        <span className="font-medium text-slate-700" data-id="element-2401" data-mp="300xu9nkt6igw">
                          {item.registered} / {item.capacity}
                        </span>
                        {item.waitlisted > 0 && <span className="text-amber-600 font-medium" data-id="element-2402" data-mp="2r4qb2dh1v1th">
                            +{item.waitlisted}
                          </span>}
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-2403" data-mp="8vi4x1jbxoh3">
                        <div className={`h-full ${fill >= 100 ? 'bg-red-500' : fill >= 80 ? 'bg-amber-400' : 'bg-[#4F46E5]'}`} style={{
                      width: `${Math.min(fill, 100)}%`
                    }} data-id="element-2404" data-mp="8xujd4pvpgpe" />
                      </div>
                    </div>
                  </td>

                  <td className="px-5 py-4 whitespace-nowrap" data-id="element-2405" data-mp="3r74sik8m218j">
                    <p className="font-medium text-slate-900" data-id="element-2406" data-mp="3da86qx7cn9yi">
                      {money(item.priceResident)}
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-2407" data-mp="2ymzpbeng68e3">
                      {money(item.priceNonResident)}{' '}
                      {t('non-résident', 'non-resident')}
                    </p>
                  </td>

                  <td className="px-5 py-4" data-id="element-2408" data-mp="37o7b4x8s1gra">
                    <button onClick={e => {
                  e.stopPropagation();
                  setHidden(prev => prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]);
                }} role="switch" aria-checked={isVisible} aria-label={t(`Visibilité de ${item.name.fr}`, `Visibility of ${item.name.en}`)} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${isVisible ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-2409" data-mp="37oz7iaa77iim">
                      <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${isVisible ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-2410" data-mp="3esdl70gpfz4d" />
                    </button>
                  </td>

                  <td className="px-5 py-4" data-id="element-2411" data-mp="2w48fvokeg63g">
                    <button onClick={e => {
                  e.stopPropagation();
                  setOpenId(item.id);
                  setAttendanceFor(item.id);
                }} aria-label={t(`Feuille de présences — ${item.name.fr}`, `Attendance sheet — ${item.name.en}`)} title={t('Feuille de présences', 'Attendance sheet')} className="p-2 rounded-lg text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all" data-id="element-2412" data-mp="2w0szmiezor0g">
                      <ClipboardCheckIcon className="w-4 h-4" data-id="element-2413" data-mp="2lx5v73beynl4" />
                    </button>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      {visible.length === 0 && <p className="px-5 py-12 text-center text-sm text-slate-500" data-id="element-2414" data-mp="1y94lvs6ev34n">
          {t('Aucune activité ne correspond à votre recherche.', 'No activity matches your search.')}
        </p>}
    </div>;
}